import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Send, MessageCircle, RefreshCw, CheckCircle2, Lock, Filter, User } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "../../lib/ist";

const STATUS_COLORS = {
  open: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  pending_customer: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  resolved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  closed: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

const STATUS_LABELS = {
  open: "Open",
  pending_customer: "Awaiting customer",
  resolved: "Resolved",
  closed: "Closed",
};

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("open");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEnd = useRef(null);

  const load = async (preserveActive = true) => {
    try {
      const { data } = await api.get("/tickets/all");
      setTickets(data);
      if (preserveActive && active) {
        const updated = data.find(t => t.id === active.id);
        if (updated) setActive(updated);
      }
    } catch { toast.error("Failed to load tickets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(false); }, []);

  // Polling — refresh every 8s when a ticket is open
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [active?.id]);

  useEffect(() => {
    if (messagesEnd.current) messagesEnd.current.scrollIntoView({ behavior: "smooth" });
  }, [active?.messages?.length]);

  const filtered = useMemo(() => {
    if (filter === "all") return tickets;
    return tickets.filter(t => (t.status || "open") === filter);
  }, [tickets, filter]);

  const send = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/tickets/${active.id}/reply`, { message: reply });
      setReply("");
      await load(true);
    } catch { toast.error("Reply failed"); }
    finally { setSending(false); }
  };

  const setStatus = async (status) => {
    if (!active) return;
    try {
      await api.post(`/tickets/${active.id}/status`, { status });
      toast.success(`Marked ${STATUS_LABELS[status]}`);
      await load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-tickets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <MessageCircle size={20} className="text-blue-400" /> Support Tickets
          </h2>
          <p className="text-slate-400 text-sm">Threaded customer support · auto-refreshes every 8s.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl bg-slate-900/60 border border-white/5 p-1" data-testid="ticket-filter">
            {[["open", "Open"], ["pending_customer", "Awaiting"], ["resolved", "Resolved"], ["closed", "Closed"], ["all", "All"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)}
                className={`px-3 py-1.5 rounded-lg text-xs transition ${filter === k ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}
                data-testid={`tf-${k}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => load(true)} className="btn-ghost !py-1.5 !px-3" data-testid="refresh-tickets"><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 space-y-2 max-h-[68vh] overflow-y-auto pr-1" data-testid="ticket-list">
          {loading ? <div className="card p-5 text-slate-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div> :
            filtered.length === 0 ? <div className="card p-5 text-slate-500 text-sm">No tickets match this filter.</div> :
              filtered.map(t => (
                <button key={t.id} onClick={() => setActive(t)}
                  className={`card p-4 w-full text-left transition border ${active?.id === t.id ? "border-blue-500/60" : "border-transparent hover:border-slate-700"}`}
                  data-testid={`ticket-row-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-white font-medium truncate flex-1">{t.subject}</div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status || "open"]}`}>{STATUS_LABELS[t.status || "open"]}</span>
                  </div>
                  <div className="text-slate-400 text-xs mt-1 flex items-center gap-2">
                    <User size={11} /> {t.user_email}
                  </div>
                  <div className="text-slate-500 text-[11px] mt-1">{fmtDateTime(t.updated_at || t.created_at)} · {(t.messages || []).length} message{(t.messages || []).length !== 1 ? "s" : ""}</div>
                </button>
              ))}
        </div>

        <div className="lg:col-span-8">
          {!active ? (
            <div className="card p-12 text-center text-slate-500 flex flex-col items-center gap-3">
              <MessageCircle size={36} className="opacity-40" />
              <div>Select a ticket on the left to start replying.</div>
            </div>
          ) : (
            <div className="card flex flex-col h-[68vh]">
              {/* Header */}
              <div className="p-4 border-b border-slate-800 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-semibold truncate">{active.subject}</h3>
                  <div className="text-xs text-slate-400 mt-1 flex items-center flex-wrap gap-2">
                    <span><User size={11} className="inline mr-1" />{active.user_email}</span>
                    <span>·</span>
                    <span>Priority: <span className="text-slate-200 capitalize">{active.priority}</span></span>
                    <span>·</span>
                    <span>Created: {fmtDateTime(active.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <select
                    value={active.status || "open"}
                    onChange={e => setStatus(e.target.value)}
                    className={`text-xs px-2.5 py-1.5 rounded-lg border bg-slate-900 ${STATUS_COLORS[active.status || "open"]}`}
                    data-testid="status-select"
                  >
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ticket-messages">
                {(active.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.by === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.by === "admin" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-200"}`}>
                      <div className={`text-[10px] uppercase tracking-wider mb-1 ${m.by === "admin" ? "text-blue-200" : "text-slate-500"}`}>
                        {m.by === "admin" ? "Optivize" : (m.name || "Customer")} · {fmtDateTime(m.at)}
                      </div>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEnd} />
              </div>

              {/* Composer */}
              {active.status === "closed" ? (
                <div className="p-4 border-t border-slate-800 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
                  <Lock size={14} /> Ticket closed — reopen by changing status above.
                </div>
              ) : (
                <div className="p-3 border-t border-slate-800 flex gap-2">
                  <textarea
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                    rows="2"
                    className="input flex-1 resize-none"
                    placeholder="Type your reply… (⌘/Ctrl + Enter to send)"
                    data-testid="admin-reply-input"
                  />
                  <button onClick={send} disabled={sending || !reply.trim()} className="btn-primary px-4 inline-flex items-center gap-2" data-testid="admin-reply-send">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
